/**
 * Tripnovac Visa — Help Center
 * Comprehensive FAQ and support resources
 *
 * @company Alpha Libra LLC
 * @domain tripnovac-visa.com
 */

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, TextField, InputAdornment, Accordion,
  AccordionSummary, AccordionDetails, Card, Grid, Paper,
  Button, Chip, Avatar, Divider, useTheme, alpha, CircularProgress, Alert, MenuItem,
} from '@mui/material';
import {
  Search, ExpandMore, HelpOutline, Email, Chat, Phone,
  FlightTakeoff, Payment, AccountCircle, Build, MenuBook,
  Public, Gavel, Security, Speed, School, Send,
} from '@mui/icons-material';
import { APP_NAME, EMAILS, COMPANY, API_BASE_URL } from '../../constants';
import { useAuth } from '../../contexts/AuthContext';

// =====================================================
// FAQ DATA
// =====================================================

interface FaqItem {
  question: string;
  answer: string;
}

interface FaqSection {
  title: string;
  icon: React.ReactElement;
  items: FaqItem[];
}

const FAQ_SECTIONS: FaqSection[] = [
  {
    title: 'Getting Started',
    icon: <FlightTakeoff />,
    items: [
      {
        question: `What is ${APP_NAME}?`,
        answer: `${APP_NAME} is an enterprise visa processing platform by Alpha Libra LLC. We provide comprehensive visa requirement data for 249 countries and territories (ISO 3166-1), AI-powered application assistance, and real-time tracking for all your travel documentation needs.`,
      },
      {
        question: 'How do I create an account?',
        answer: 'Click the "Register" button on the top right of the homepage. Fill in your details including name, email, and password. You will receive a verification email — click the link to activate your account and start exploring visa requirements.',
      },
      {
        question: 'Is there a free tier available?',
        answer: 'Yes! Our Free tier allows you to browse 249 countries and territories (ISO 3166-1), view visa requirements, and submit one application per month. Upgrade to Pro, Business, or Enterprise for additional features like AI coaching, priority processing, and team management.',
      },
      {
        question: 'Which countries does the platform cover?',
        answer: 'We maintain visa requirement data for 249 countries and territories (ISO 3166-1 English short names), covering more than 2,000 visa types. Our data is updated regularly to reflect the latest immigration policy changes.',
      },
    ],
  },
  {
    title: 'Visa Applications',
    icon: <Public />,
    items: [
      {
        question: 'How do I apply for a visa through the platform?',
        answer: 'Navigate to the country you wish to visit, select the appropriate visa type, and click "Apply." Our guided form will walk you through document requirements, fees, and submission steps. You can save progress and return later.',
      },
      {
        question: 'What documents are typically required?',
        answer: 'Requirements vary by country and visa type but commonly include a valid passport, passport-sized photos, proof of financial means, travel itinerary, accommodation booking, and a completed application form. Our platform lists exact requirements for each visa.',
      },
      {
        question: 'How long does visa processing take?',
        answer: 'Processing times vary by country and visa type — typically between 3 to 30 business days. Premium and Business tier users benefit from priority processing options. Real-time tracking keeps you informed at every step.',
      },
      {
        question: 'Can I track my application status?',
        answer: 'Yes. Navigate to Dashboard → My Applications to view the status recorded in Tripnovac. This is a platform workflow status and may not be a live government-authority status.',
      },
    ],
  },
  {
    title: 'Payments & Credits',
    icon: <Payment />,
    items: [
      {
        question: 'What payment methods are accepted?',
        answer: 'We accept all major credit and debit cards (Visa, Mastercard, American Express) through our secure Stripe payment gateway. Enterprise customers may also pay via invoice.',
      },
      {
        question: 'How does the credit system work?',
        answer: 'Credits are used for premium features like AI interview coaching, priority processing, and additional applications beyond your tier limit. Credits can be purchased in bundles and never expire.',
      },
      {
        question: 'Can I get a refund?',
        answer: 'Subscription refunds are available within 14 days of purchase if no premium features have been used. Credit purchases are non-refundable but transferable within your organization on Business and Enterprise plans.',
      },
    ],
  },
  {
    title: 'Account Management',
    icon: <AccountCircle />,
    items: [
      {
        question: 'How do I reset my password?',
        answer: 'Click "Forgot Password" on the login page, enter your registered email, and follow the reset link sent to your inbox. The link expires after 1 hour for security.',
      },
      {
        question: 'Can I change my subscription tier?',
        answer: 'Yes. Go to Settings → Subscription to upgrade or downgrade your plan at any time. Upgrades take effect immediately; downgrades apply at the end of your current billing cycle.',
      },
      {
        question: 'How do I manage team members?',
        answer: 'Business and Enterprise plans support multi-user management. Navigate to Settings → Team to invite members, assign roles (Admin, Manager, Agent), and manage permissions.',
      },
      {
        question: 'How do I delete my account?',
        answer: 'Go to Settings → Account → Delete Account. This action is permanent and will remove all your data after a 30-day grace period. Contact support if you need assistance.',
      },
    ],
  },
  {
    title: 'Technical Issues',
    icon: <Build />,
    items: [
      {
        question: 'The platform is loading slowly — what should I do?',
        answer: 'Try clearing your browser cache, disabling extensions, or switching to a supported browser (Chrome, Firefox, Edge, Safari). If the issue persists, check our status page or contact support.',
      },
      {
        question: 'I cannot upload my documents — what formats are accepted?',
        answer: 'We accept PDF, JPG, PNG, and WEBP files up to 10 MB each. Ensure your file is not corrupted and that your internet connection is stable. Try a different browser if the issue continues.',
      },
      {
        question: 'Is my data secure?',
        answer: 'Production traffic uses HTTPS, protected routes require authenticated role access, Stripe handles card details, and configured document storage uses private time-limited links. No online system can guarantee absolute security.',
      },
    ],
  },
];

const KNOWLEDGE_BASE = [
  { title: 'Visa Guides', icon: <MenuBook />, count: 120, color: '#2563EB' },
  { title: 'Country Policies', icon: <Gavel />, count: 250, color: '#7C3AED' },
  { title: 'Security & Privacy', icon: <Security />, count: 18, color: '#059669' },
  { title: 'API Documentation', icon: <Speed />, count: 45, color: '#D97706' },
  { title: 'Tutorials', icon: <School />, count: 32, color: '#DC2626' },
  { title: 'Release Notes', icon: <Build />, count: 60, color: '#0891B2' },
];

// =====================================================
// COMPONENT
// =====================================================

const HelpPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedPanel, setExpandedPanel] = useState<string | false>(false);
  const [ticketForm, setTicketForm] = useState({
    name: user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '',
    email: user?.email || '',
    subject: 'Technical Support',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [ticketMsg, setTicketMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const filteredSections = useMemo(() => {
    if (!searchQuery.trim()) return FAQ_SECTIONS;
    const q = searchQuery.toLowerCase();
    return FAQ_SECTIONS.map((section) => ({
      ...section,
      items: section.items.filter(
        (item) =>
          item.question.toLowerCase().includes(q) ||
          item.answer.toLowerCase().includes(q),
      ),
    })).filter((section) => section.items.length > 0);
  }, [searchQuery]);

  const handleAccordionChange = (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedPanel(isExpanded ? panel : false);
  };

  const submitTicket = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ticketForm.email || !ticketForm.subject || !ticketForm.message) {
      setTicketMsg({ type: 'error', text: 'Please fill in email, subject, and message.' });
      return;
    }
    setSubmitting(true);
    setTicketMsg(null);
    try {
      const res = await fetch(`${API_BASE_URL}/support/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          name: ticketForm.name,
          email: ticketForm.email,
          subject: ticketForm.subject,
          message: ticketForm.message,
          category: 'help_center',
          source: 'help_page',
        }),
      });
      const payload = await res.json().catch(() => null);
      if (!res.ok) throw new Error(payload?.message || payload?.error || 'Failed to submit');
      setTicketMsg({ type: 'success', text: 'Support request sent. Check your email for confirmation.' });
      setTicketForm((p) => ({ ...p, message: '' }));
    } catch (err: any) {
      setTicketMsg({ type: 'error', text: err?.message || 'Could not submit. Email support@tripnovac.com.' });
    } finally {
      setSubmitting(false);
    }
  };

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      bgcolor: '#fff',
      color: '#0F172A',
      borderRadius: 2,
      '& fieldset': { borderColor: 'rgba(15,23,42,0.2)' },
      '&:hover fieldset': { borderColor: 'rgba(15,23,42,0.4)' },
      '&.Mui-focused fieldset': { borderColor: '#2563EB' },
    },
    '& .MuiInputBase-input': { color: '#0F172A' },
    '& .MuiInputLabel-root': { color: '#475569' },
    '& .MuiInputLabel-root.Mui-focused': { color: '#2563EB' },
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* ── Hero Section ── */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1E3A5F 0%, #0F2744 60%, #0A1929 100%)',
          color: '#fff',
          py: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'radial-gradient(circle at 30% 50%, rgba(59,130,246,0.15) 0%, transparent 60%)',
          }}
        />
        <Container maxWidth="md" sx={{ position: 'relative', textAlign: 'center' }}>
          <HelpOutline sx={{ fontSize: 56, mb: 2, opacity: 0.9 }} />
          <Typography variant="h3" fontWeight={800} gutterBottom>
            How can we help you?
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.8, mb: 4, maxWidth: 600, mx: 'auto' }}>
            Search our knowledge base or browse frequently asked questions below.
          </Typography>
          <TextField
            fullWidth
            placeholder="Search for answers…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: '#64748B' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              maxWidth: 600,
              mx: 'auto',
              '& .MuiOutlinedInput-root': {
                bgcolor: '#fff',
                color: '#0F172A',
                borderRadius: 3,
                '& fieldset': { border: 'none' },
              },
              '& .MuiInputBase-input': { color: '#0F172A' },
              '& .MuiInputBase-input::placeholder': { color: '#64748B', opacity: 1 },
            }}
          />
        </Container>
      </Box>

      {/* ── FAQ Accordion Sections ── */}
      <Container maxWidth="md" sx={{ py: { xs: 6, md: 10 } }}>
        {filteredSections.length === 0 && (
          <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
            <Typography variant="h6" gutterBottom>
              No results found
            </Typography>
            <Typography color="text.secondary">
              Try a different search term or browse the categories below.
            </Typography>
          </Paper>
        )}

        {filteredSections.map((section, sIdx) => (
          <Box key={section.title} sx={{ mb: 5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Avatar
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: 'primary.main',
                  width: 40,
                  height: 40,
                }}
              >
                {section.icon}
              </Avatar>
              <Typography variant="h5" fontWeight={700}>
                {section.title}
              </Typography>
              <Chip label={`${section.items.length} questions`} size="small" />
            </Box>

            {section.items.map((item, qIdx) => {
              const panelId = `s${sIdx}-q${qIdx}`;
              return (
                <Accordion
                  key={panelId}
                  expanded={expandedPanel === panelId}
                  onChange={handleAccordionChange(panelId)}
                  disableGutters
                  elevation={0}
                  sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: '12px !important',
                    mb: 1.5,
                    '&:before': { display: 'none' },
                    overflow: 'hidden',
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 3, py: 1 }}>
                    <Typography fontWeight={600}>{item.question}</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 3, pb: 3 }}>
                    <Typography color="text.secondary" sx={{ lineHeight: 1.8 }}>
                      {item.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        ))}
      </Container>

      {/* ── Knowledge Base Categories ── */}
      <Box sx={{ bgcolor: alpha(theme.palette.primary.main, 0.03), py: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg">
          <Typography variant="h4" fontWeight={800} textAlign="center" gutterBottom>
            Knowledge Base
          </Typography>
          <Typography color="text.secondary" textAlign="center" sx={{ mb: 5, maxWidth: 500, mx: 'auto' }}>
            Explore our comprehensive documentation and guides.
          </Typography>

          <Grid container spacing={3}>
            {KNOWLEDGE_BASE.map((cat) => (
              <Grid item xs={6} sm={4} md={2} key={cat.title}>
                <Paper
                  elevation={0}
                  onClick={() => setSearchQuery(cat.title.split(' ')[0])}
                  sx={{
                    p: 3,
                    textAlign: 'center',
                    borderRadius: 3,
                    border: `1px solid ${theme.palette.divider}`,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': { borderColor: cat.color, transform: 'translateY(-4px)', boxShadow: 4 },
                  }}
                >
                  <Avatar sx={{ bgcolor: alpha(cat.color, 0.1), color: cat.color, mx: 'auto', mb: 1.5, width: 48, height: 48 }}>
                    {cat.icon}
                  </Avatar>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {cat.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {cat.count} articles
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── Still Need Help? ── */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <Typography variant="h4" fontWeight={800} textAlign="center" gutterBottom>
          Still need help?
        </Typography>
        <Typography color="text.secondary" textAlign="center" sx={{ mb: 5, maxWidth: 500, mx: 'auto' }}>
          Our support team is here for you. Choose the channel that works best.
        </Typography>

        <Grid container spacing={4} justifyContent="center">
          {[
            {
              icon: <Email sx={{ fontSize: 36 }} />,
              title: 'Email Support',
              description: 'Get a response within 24 hours',
              action: EMAILS.SUPPORT,
              color: '#2563EB',
              onClick: () => { window.location.href = `mailto:${EMAILS.SUPPORT}`; },
            },
            {
              icon: <Chat sx={{ fontSize: 36 }} />,
              title: 'Contact Us',
              description: 'Send us a message',
              action: 'Contact Form',
              color: '#7C3AED',
              onClick: () => navigate('/contact'),
            },
            {
              icon: <Phone sx={{ fontSize: 36 }} />,
              title: 'Phone Support',
              description: 'For Enterprise & Business plans',
              action: COMPANY.PHONE,
              color: '#059669',
              onClick: () => { window.location.href = 'tel:+18004640701'; },
            },
          ].map((channel) => (
            <Grid item xs={12} sm={4} key={channel.title}>
              <Card
                elevation={0}
                sx={{
                  textAlign: 'center',
                  p: 4,
                  borderRadius: 4,
                  border: `1px solid ${theme.palette.divider}`,
                  transition: 'all 0.2s',
                  '&:hover': { borderColor: channel.color, boxShadow: 6 },
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: alpha(channel.color, 0.1),
                    color: channel.color,
                    width: 64,
                    height: 64,
                    mx: 'auto',
                    mb: 2,
                  }}
                >
                  {channel.icon}
                </Avatar>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  {channel.title}
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                  {channel.description}
                </Typography>
                <Button variant="outlined" onClick={channel.onClick} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
                  {channel.action}
                </Button>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* ── Submit a support ticket ── */}
      <Box sx={{ bgcolor: alpha(theme.palette.primary.main, 0.03), py: { xs: 6, md: 8 } }}>
        <Container maxWidth="md">
          <Typography variant="h4" fontWeight={800} textAlign="center" gutterBottom>
            Submit a support request
          </Typography>
          <Typography color="text.secondary" textAlign="center" sx={{ mb: 4, maxWidth: 520, mx: 'auto' }}>
            Creates a tracked ticket for our team and sends email confirmation when SendGrid is configured.
          </Typography>
          {ticketMsg && (
            <Alert severity={ticketMsg.type} sx={{ mb: 2 }} onClose={() => setTicketMsg(null)}>
              {ticketMsg.text}
            </Alert>
          )}
          <Paper
            component="form"
            onSubmit={submitTicket}
            elevation={0}
            sx={{ p: { xs: 3, md: 4 }, borderRadius: 3, border: `1px solid ${theme.palette.divider}`, bgcolor: '#fff' }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Full name"
                  fullWidth
                  value={ticketForm.name}
                  onChange={(e) => setTicketForm((p) => ({ ...p, name: e.target.value }))}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email"
                  type="email"
                  required
                  fullWidth
                  value={ticketForm.email}
                  onChange={(e) => setTicketForm((p) => ({ ...p, email: e.target.value }))}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Subject"
                  select
                  required
                  fullWidth
                  value={ticketForm.subject}
                  onChange={(e) => setTicketForm((p) => ({ ...p, subject: e.target.value }))}
                  sx={fieldSx}
                >
                  {['General Inquiry', 'Visa Application Help', 'Billing & Payments', 'Technical Support', 'Bug Report', 'Feature Request'].map((opt) => (
                    <MenuItem key={opt} value={opt} sx={{ color: '#0F172A' }}>
                      {opt}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="How can we help?"
                  required
                  fullWidth
                  multiline
                  rows={4}
                  value={ticketForm.message}
                  onChange={(e) => setTicketForm((p) => ({ ...p, message: e.target.value }))}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={submitting}
                  endIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <Send />}
                  sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, px: 4 }}
                >
                  {submitting ? 'Sending…' : 'Send support request'}
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Container>
      </Box>

      {/* ── Footer Note ── */}
      <Divider />
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          © {new Date().getFullYear()} {APP_NAME} by Alpha Libra LLC. All rights reserved.
        </Typography>
      </Box>
    </Box>
  );
};

export default HelpPage;
